import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/*
 * Horizon marquee with the Cut to Measure strip changes:
 * - Constant speed: when the element has data-speed (pixels per second), one loop lasts
 *   (content width + gap) / speed seconds, so adding items no longer speeds the text up.
 *   Marquees without data-speed (blocks/_marquee.liquid) keep Horizon's formula.
 * - Every copy (repeated sets and the duplicated content) is aria-hidden and inert, so screen
 *   readers read the first set once and hidden copies can't take focus.
 * - Reduced motion: no copies are built; the section's CSS shows one static row.
 * - Pausing (WCAG 2.2.2): a .marquee__pause button (aria-pressed) pauses the marquees on the page
 *   and the choice is kept in sessionStorage for the visit. CSS also pauses on hover and
 *   :focus-within; this file flags off-screen (data-offscreen) and hidden tabs (data-doc-hidden).
 * Theme editor: document listeners are added once behind a window flag. A reloaded section brings
 * a new element, which builds itself on connect and re-reads the stored choice.
 */

const PAUSE_STORAGE_KEY = 'zf-marquee-paused';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** The shopper's pause choice. Storage can be blocked, so this in-memory copy is the source of truth. */
let pausedByShopper = readStoredPause();

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The first set of items (copies carry no ref).
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {number | null} */
  #marqueeWidth = null;

  connectedCallback() {
    super.connectedCallback();

    this.syncPause();
    this.toggleAttribute('data-doc-hidden', document.hidden);
    this.#observeVisibility();

    window.addEventListener('resize', this.#handleResize);
    reducedMotion.addEventListener?.('change', this.#handleMotionChange);

    this.#build();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    reducedMotion.removeEventListener?.('change', this.#handleMotionChange);
    this.#handleResize.cancel();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  /**
   * This marquee's own pause button, if the section renders one.
   * @returns {HTMLButtonElement | null}
   */
  get pauseButton() {
    const button = this.querySelector('.marquee__pause');

    return button instanceof HTMLButtonElement && button.closest('marquee-component') === this ? button : null;
  }

  /**
   * Applies the shopper's pause choice to this marquee and its button. Only a marquee with a
   * visible way to resume is held paused.
   */
  syncPause() {
    const button = this.pauseButton;
    const paused = Boolean(button) && pausedByShopper;

    this.toggleAttribute('data-paused', paused);
    button?.setAttribute('aria-pressed', String(paused));
  }

  /**
   * Stores the shopper's choice and applies it to every marquee on the page.
   * @param {boolean} paused
   */
  setPaused(paused) {
    storePause(paused);

    for (const marquee of document.querySelectorAll('marquee-component')) {
      if (marquee instanceof MarqueeComponent) marquee.syncPause();
    }
  }

  #handleResize = debounce(() => {
    this.#build({ requireWidthChange: true, restart: true });
  }, 250);

  #handleMotionChange = () => {
    this.#build({ restart: true });
  };

  #observeVisibility() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;

    if (typeof IntersectionObserver === 'undefined') return;

    this.#visibilityObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) this.toggleAttribute('data-offscreen', !entry.isIntersecting);
    });
    this.#visibilityObserver.observe(this);
  }

  /**
   * Builds the copies that make the loop seamless and sets the speed. Under reduced motion it only
   * removes copies.
   * @param {{ requireWidthChange?: boolean, restart?: boolean }} [options]
   */
  async #build({ requireWidthChange = false, restart = false } = {}) {
    const firstSet = this.refs.marqueeItems?.[0];
    if (!firstSet) return;

    if (reducedMotion.matches) {
      this.#removeCopies();
      return;
    }

    const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies(firstSet);

    if (!this.isConnected || reducedMotion.matches) return;
    // Opt out of marquee manipulation on vertical resizes.
    if (requireWidthChange && !isHorizontalResize) return;

    this.#removeCopies();
    this.#addRepeatedItems(firstSet, numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));

    if (restart) this.#restartAnimation();
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  /**
   * @param {Element} firstSet
   * @returns {Promise<{ numberOfCopies: number, isHorizontalResize: boolean }>}
   */
  #queryNumberOfCopies(firstSet) {
    return new Promise((resolve) => {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          // Wrapping the resolve in a setTimeout splits each marquee reflow into a separate task.
          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeItemsWidth)),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(firstSet);
    });
  }

  /**
   * Seconds for one loop. The keyframes move the track by one content width plus the gap.
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const pixelsPerSecond = Number(this.dataset.speed);

    if (pixelsPerSecond > 0) {
      const { content, wrapper } = this.refs;
      const gap = parseFloat(getComputedStyle(wrapper).columnGap) || 0;
      const distance = content.getBoundingClientRect().width + gap;

      return Math.max(distance / pixelsPerSecond, 1);
    }

    // Horizon's original formula, for marquees without data-speed.
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  /** Removes every copy, leaving the first set inside the content. */
  #removeCopies() {
    const { wrapper, content, marqueeItems } = this.refs;
    const firstSet = marqueeItems?.[0];

    for (const child of Array.from(wrapper.children)) {
      if (child !== content) child.remove();
    }

    for (const child of Array.from(content.children)) {
      if (child !== firstSet) child.remove();
    }
  }

  /**
   * @param {Element} firstSet
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(firstSet, numberOfCopies) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      content.appendChild(hideCopy(firstSet.cloneNode(true)));
    }
  }

  #duplicateContent() {
    const { content, wrapper } = this.refs;
    const clone = hideCopy(content.cloneNode(true));

    if (clone instanceof Element) {
      for (const set of clone.querySelectorAll(':scope > [ref]')) {
        set.removeAttribute('ref');
      }
    }

    wrapper.appendChild(clone);
  }
}

/**
 * Marks a decorative copy: out of the accessibility tree, unfocusable, and without the ref that
 * would make Component treat it as one of the marquee's own elements.
 * @param {Node} node
 * @returns {Node}
 */
function hideCopy(node) {
  if (node instanceof Element) {
    node.removeAttribute('ref');
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('inert', '');
  }

  return node;
}

/** @returns {boolean} */
function readStoredPause() {
  try {
    return window.sessionStorage.getItem(PAUSE_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

/**
 * @param {boolean} paused
 */
function storePause(paused) {
  pausedByShopper = paused;

  try {
    if (paused) {
      window.sessionStorage.setItem(PAUSE_STORAGE_KEY, 'true');
    } else {
      window.sessionStorage.removeItem(PAUSE_STORAGE_KEY);
    }
  } catch (error) {
    // Storage blocked (private mode, sandboxed preview): the choice lasts until the page reloads.
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}

if (!window.zfMarqueeListeners) {
  window.zfMarqueeListeners = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.marquee__pause');
    const marquee = button?.closest('marquee-component');
    if (!button || !(marquee instanceof MarqueeComponent)) return;

    marquee.setPaused(button.getAttribute('aria-pressed') !== 'true');
  });

  document.addEventListener('visibilitychange', () => {
    for (const marquee of document.querySelectorAll('marquee-component')) {
      marquee.toggleAttribute('data-doc-hidden', document.hidden);
    }
  });

  // Theme editor: re-apply the stored choice to marquees in a reloaded section, including an element
  // that was updated in place rather than replaced.
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target instanceof Element ? event.target : document;

    for (const marquee of root.querySelectorAll('marquee-component')) {
      if (marquee instanceof MarqueeComponent) marquee.syncPause();
    }
  });
}
